<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import { page } from '$app/state';
	import { binToHex, cashAddressToLockingBytecode, encodeTransactionBch } from '@bitauth/libauth';

	import { ElectrumClient, ConnectionStatus } from '@electrum-cash/network';

	import { IndexedDBProvider } from '@mainnet-cash/indexeddb-storage';
	import {
		BaseWallet,
		Connection,
		Wallet,
		TestNetWallet,
		NFTCapability,
		TokenSendRequest
	} from '@unspent/wallet';
	import { blo } from 'blo';

	import {
		cashAssemblyToHex,
		getScriptHash,
		getHdPrivateKey,
		type UtxoI,
		getAllTransactions,
		sumSourceOutputValue,
		sumUtxoValue
	} from '@unspent/tau';

	import { Channel, Post, buildChannel, parseUsername } from '@fbch/lib';

	import trash from '$lib/images/trash.svg';
	import BitauthLink from '$lib/BitauthLink.svelte';
	import ChatPost from '$lib/ChatPost.svelte';
	import CONNECTED from '$lib/images/connected.svg';
	import DISCONNECTED from '$lib/images/disconnected.svg';

	const isMainnet = page.url.hostname == 'vox.cash';
	const prefix = isMainnet ? 'bitcoincash' : 'bchtest';
	const server = isMainnet ? 'electrum.imaginary.cash' : 'chipnet.bch.ninja';
	const fee = isMainnet ? 1 : 10;
	const explorer = isMainnet
		? 'https://explorer.salemkode.com/address/'
		: 'https://cbch.loping.net/address/';

	const protocol_prefix = cashAssemblyToHex(`OP_RETURN <"${Channel.PROTOCOL_IDENTIFIER}">`);

	let now = $state(0);
	let balance = $state(0);
	let contractBalance = $state(0);
	let connectionStatus = $state('');
	let contractState = $state('');

	let { topic = $bindable() } = $props();

	let message = $state('');
	let thisAuth = $state('');
	let sequence = $state(0);
	let estimate = $state(0);
	let showSettings = $state(false);
	let showAll = $state(true);

	const scripthash = $derived(Channel.getScriptHash(topic));
	const contractAddress = $derived(Channel.getAddress(topic, prefix));

	let posts: any[] = $state([]);
	let transactions: any[] = $state([]);

	let timer: any;
	let key = '';
	let electrumClient: any;
	let walletScriptHash = '';

	let wallet: any;
	let walletUnspent: any[] = $state([]);

	const debounceUpdateWallet = () => {
		clearTimeout(timer);
		timer = setTimeout(() => {
			updateWallet();
			updateContract();
		}, 1500);
	};

	const handleNotifications = async function (data: any) {
		if (data.method === 'blockchain.headers.subscribe') {
			let d = data.params[0];
			now = d.height;
		} else if (data.method === 'blockchain.scripthash.subscribe') {
			if (data.params[1] !== contractState) {
				contractState = data.params[1];
				connectionStatus = ConnectionStatus[electrumClient.status];
				debounceUpdateWallet();
			}
		} else {
			console.log(data);
		}
	};

	const updateScroll = function () {
		let chat = document.getElementById('chat')!;
		var xH = chat.scrollHeight;
		chat.scrollTo(0, xH);
	};

	const likePost = async function (postId: string) {
		let likePostTx = Channel.like(
			topic,
			postId,
			walletUnspent[0],
			(Math.round(now / 1000) + 10) * 10,
			key,
			fee
		);

		let raw_tx = binToHex(encodeTransactionBch(likePostTx.transaction));
		await broadcast(raw_tx);
	};

	const reply = async function (postId: string, message: string) {
		let replyPostTx = Channel.reply(
			topic,
			postId,
			message,
			walletUnspent[0],
			(Math.round(now / 1000) + 10) * 10,
			key
		);

		let raw_tx = binToHex(encodeTransactionBch(replyPostTx.transaction));
		await broadcast(raw_tx);
	};

	const setHeight = async function () {
		let response = await electrumClient.request('blockchain.headers.get_tip');
		if (response instanceof Error) throw response;
		now = response.height;
	};

	const updateWallet = async function () {
		let response = await electrumClient.request(
			'blockchain.scripthash.listunspent',
			walletScriptHash,
			'include_tokens'
		);
		if (response instanceof Error) throw response;
		balance = sumUtxoValue(response);

		walletUnspent = response.filter(
			(u: UtxoI) =>
				u.token_data && u.token_data.nft && u.token_data.nft.commitment.startsWith(protocol_prefix)
		);
		if (walletUnspent.length > 0 && walletUnspent[0].token_data) {
			thisAuth = walletUnspent[0].token_data.category;
		}
	};

	const emptyBaton = async function () {
		let response = await wallet.send([
			new TokenSendRequest({
				cashaddr: wallet.getTokenDepositAddress(),
				category: walletUnspent[0].token_data.category,
				amount: 0n,
				nft: {
					capability: walletUnspent[0].token_data.nft.capability,
					commitment: walletUnspent[0].token_data.nft.commitment
				},
				value: 800n
			})
		]);
		console.log(response);
	};

	const clearPosts = async function () {
		let response = await electrumClient.request(
			'blockchain.scripthash.listunspent',
			scripthash,
			'include_tokens'
		);
		if (response instanceof Error) throw response;
		let old = response.filter((u: UtxoI) => u.height > 0 && now - u.height > 1000).slice(0, 300);
		if (old.length > 0) {
			let clearPostTx = Channel.clear(topic, old, walletUnspent[0], key, now);
			let raw_tx = binToHex(encodeTransactionBch(clearPostTx.transaction));
			console.log(raw_tx);
			await broadcast(raw_tx);
		}
	};

	const burnSpam = async function () {
		let response = await electrumClient.request(
			'blockchain.scripthash.listunspent',
			scripthash,
			'include_tokens'
		);
		if (response instanceof Error) throw response;

		let spam = response.filter((u: UtxoI) => Math.floor(u.value / 10) * 1000 - u.height < 1000);
		if (spam.length > 0) {
			let clearPostTx = Channel.clear(topic, spam, walletUnspent[0], key, now);
			let raw_tx = binToHex(encodeTransactionBch(clearPostTx.transaction));
			console.log(raw_tx);
			await broadcast(raw_tx);
		}
	};

	const updateContract = async function () {
		let unspentResponse = await electrumClient.request(
			'blockchain.scripthash.listunspent',
			scripthash,
			'include_tokens'
		);

		if (unspentResponse instanceof Error) throw unspentResponse;
		contractBalance = sumUtxoValue(unspentResponse);

		let tx_hashes = Array.from(
			new Set(unspentResponse.map((utxo: any) => utxo.tx_hash))
		) as string[];

		let historyResponse = await electrumClient.request(
			'blockchain.scripthash.get_history',
			scripthash,
			now - 10000,
			-1
		);

		//if (showAll) tx_hashes = historyResponse.map(r => r.tx_hash)
		transactions = await getAllTransactions(electrumClient, tx_hashes);

		posts = buildChannel(historyResponse, transactions, topic);
		posts = posts.map((p) => {
			return {
				thisAuth: thisAuth == p.auth,
				...p
			};
		});

		// update the current sequence state to match the chan
		if (posts.slice(-1).length > 0) {
			sequence = posts.slice(-1)[0].height <= 0 ? posts.slice(-1)[0].sequence + 1 : 0;
		} else {
			sequence = 0;
		}
	};

	const clearPost = async function (post: Post) {
		let response = await electrumClient.request(
			'blockchain.scripthash.listunspent',
			scripthash,
			'include_tokens'
		);
		if (response instanceof Error) throw response;

		let utxos = response.filter((u: UtxoI) => u.tx_hash == post.hash).slice(0, 200);
		let clearPostTx = Channel.clear(topic, utxos, walletUnspent[0], key, now);
		let raw_tx = binToHex(encodeTransactionBch(clearPostTx.transaction));
		await broadcast(raw_tx);
	};

	const debounceEstimate = () => {
		clearTimeout(timer);
		timer = setTimeout(() => {
			estimate = reEstimate(message);
		}, 500);
	};

	const reEstimate = function (msg: string) {
		if (msg.length > 0) {
			let post = Channel.post(
				topic,
				msg,
				walletUnspent[0],
				(Math.round(now / 1000) + 10) * 10,
				key,
				sequence
			);
			const returned = post.transaction.outputs[post.transaction.outputs.length - 1].valueSatoshis;
			return Number(sumSourceOutputValue(post.sourceOutputs) - returned);
		} else {
			return 0;
		}
	};

	const send = async function (msg: string) {
		let minValue = (Math.round(now / 1000) + 10) * 10;

		let post = Channel.post(topic, msg, walletUnspent[0], minValue, key, sequence, [], fee);

		let raw_tx = binToHex(encodeTransactionBch(post.transaction));
		await broadcast(raw_tx);
		message = '';
	};

	const broadcast = async function (raw_tx: string) {
		let response = await electrumClient.request('blockchain.transaction.broadcast', raw_tx);
		sequence += 1;
		if (response instanceof Error) {
			connectionStatus = ConnectionStatus[electrumClient.status];
			throw response;
		}
		response as any[];
	};

	const newAuthBaton = async function () {
		await wallet.sendMax(wallet.getDepositAddress());
		let uname = cashAssemblyToHex(`OP_RETURN <"${Channel.PROTOCOL_IDENTIFIER}"> <"pseudonymous">`);
		let sendResponse = await wallet.tokenGenesis({
			cashaddr: wallet.getTokenDepositAddress()!, // token UTXO recipient, if not specified will default to sender's address
			nft: {
				commitment: uname, 
				capability: NFTCapability.minting 
			},
			value: 1_000_000 // Satoshi value
		});
	};

	const topUp = async function (amount: number) {
		balance = walletUnspent[0].value;
		thisAuth = walletUnspent[0].token_data.category;
		let uname = cashAssemblyToHex(`OP_RETURN <"${Channel.PROTOCOL_IDENTIFIER}"> <"pseudonymous">`);

		let sendResponse = await wallet.send(
			new TokenSendRequest({
				cashaddr: wallet.getTokenDepositAddress()!,
				category: thisAuth,
				nft: {
					commitment: uname, // NFT Commitment message
					capability: NFTCapability.minting // NFT capability
				},
				value: BigInt(balance + amount) // Satoshi value
			})
		);
		await updateWallet();
	};

	onMount(async () => {
		BaseWallet.StorageProvider = IndexedDBProvider;
		wallet = isMainnet ? await Wallet.named(`vox`) : await TestNetWallet.named(`vox`);
		if (isMainnet) {
			let conn = new Connection('mainnet', 'wss://electrum.imaginary.cash:50004');
			wallet.provider = conn.networkProvider;
			globalThis.BCH = conn.networkProvider
		} else {
			let conn = new Connection('testnet', 'wss://chipnet.bch.ninja:50004');
			//let conn = new Connection('testnet', 'wss://chipnet.imaginary.cash:50004');
			wallet.provider = conn.networkProvider;
			globalThis.tBCH = conn.networkProvider
		}
		key = getHdPrivateKey(wallet.mnemonic!, wallet.derivationPath.slice(0, -2), wallet.isTestnet);
		let bytecodeResult = cashAddressToLockingBytecode(wallet.getDepositAddress());
		if (typeof bytecodeResult == 'string') throw bytecodeResult;
		walletScriptHash = getScriptHash(bytecodeResult.bytecode);

		// Initialize an electrum client.
		electrumClient = new ElectrumClient(Channel.USER_AGENT, '1.4.1', server);

		// Wait for the client to connect.
		await electrumClient.connect();
		// Set up a callback function to handle new blocks.

		// Listen for notifications.
		electrumClient.on('notification', handleNotifications);
		connectionStatus = ConnectionStatus[electrumClient.status];
		// Set up a subscription for new block headers.
		await electrumClient.subscribe('blockchain.scripthash.subscribe', scripthash);
		await electrumClient.subscribe('blockchain.scripthash.subscribe', walletScriptHash);
		await setHeight();
		await updateWallet();
		await updateContract();
		updateScroll();
	});

	onDestroy(async () => {
		await wallet.provider.disconnect();
		await electrumClient.disconnect();
	});
</script>

<div class="box">
	<div class="row header">
		{now.toLocaleString()}<sub>■</sub>
		{sequence}

		<div style="flex: 2 2 auto;"></div>
		<b><a onclick={() => (topic = '')} href="/pop/">pop</a> {topic}</b>
		<div style="flex: 2 2 auto;"></div>
		{#if contractBalance}
			<a target="_blank" href="{explorer}{contractAddress}">
				{Math.floor(Number(contractBalance) / 1000).toLocaleString()}k sats &nbsp;
			</a>
		{/if}
		<BitauthLink template={Channel.template} />
		{#if connectionStatus == 'CONNECTED'}
			<img src={CONNECTED} alt={connectionStatus} />
		{:else}
			<img src={DISCONNECTED} alt="Disconnected" />
		{/if}
	</div>
	<div id="chat" class="row content">
		{#if transactions}
			{#await transactions then build}
				{#each posts as post}
					<ChatPost {likePost} {...post} />
					{#if showSettings}
						<div class="deleteMe">
							<button onclick={() => clearPost(post)}>
								<img height="36px" src={trash} />
							</button>
						</div>
					{/if}
				{/each}
			{:catch error}
				<p style="color: red">{error.message}</p>
			{/await}
		{/if}
	</div>
	{#if balance > 1000000 && walletUnspent.length == 0}
		<div class="row footer">
			<h2>Create new identity</h2>
			<button onclick={() => newAuthBaton()}>New identity 1M sats</button>
		</div>
	{:else if balance < 1100000 && walletUnspent.length == 0}
		<div class="row footer">
			<p><a href="/wallet">Deposit funds</a> to create identity</p>
		</div>
	{:else}
		<div class="row footer">
			{#if connectionStatus == 'CONNECTED'}
				<div class="edit">
					<textarea onkeyup={() => debounceEstimate()} bind:value={message}> </textarea>
					<div class="estimate">
						{estimate.toLocaleString()} sats
					</div>
				</div>
				<div class="send">
					<div class="auth">
						{#if thisAuth}
							<img height="32px" src={blo(thisAuth, 16)} alt="avatar" />
						{/if}
						{#if walletUnspent.length > 0}
							<div style="font-size:x-small;">
								{parseUsername(walletUnspent[0].token_data.nft.commitment)}<br />
								{walletUnspent[0].value.toLocaleString()}sats
							</div>
						{/if}
					</div>
					<button onclick={() => send(message)}>Send</button>
				</div>
			{:else}
				<p>Not connected?</p>
			{/if}
		</div>
	{/if}

	<div class="row footer">
		<div style="margin: auto;">Advanced</div>
		<div>
			<!-- Rounded switch -->
			<label class="switch">
				<input type="checkbox" bind:checked={showSettings} />
				<span class="slider round"></span>
			</label>
		</div>
	</div>

	{#if showSettings}
		{#if walletUnspent.length > 0}
			<div class="row footer">
				<button onclick={() => topUp(10000000)}>Top up 10M sats</button>
				<button onclick={() => topUp(1000000)}>Top up 1M sats</button>
			</div>
			<div class="row footer">
				<button onclick={() => clearPosts()}>Clear Old Posts</button>
				<button onclick={() => burnSpam()}>Burn Spam</button>
			</div>
			<div class="row footer">
				{#if thisAuth}
					<button onclick={() => emptyBaton()}>Empty Baton Sats</button>
				{/if}
			</div>
		{/if}
	{/if}
</div>

<style>
	.box {
		display: flex;
		flex: 1 1 auto;
		flex-flow: column;
		height: 100%;
		border-radius: 10px;
		border: 1px solid rgba(78, 11, 92, 0.452);
		background-color: #ffffff33;
	}

	.box .row {
		border: 1px dotted grey;
	}

	.box .row.header {
		padding: 3px;
		display: flex;
		color: #ff00ff77;
		font-weight: 800;
		text-align: center;
		flex: 0 1 auto;

		overflow: hidden;
		/* The above is shorthand for:
        flex-grow: 0,
        flex-shrink: 1,
        flex-basis: auto
        */
	}

	.box .row.header b {
		overflow: hidden;
	}

	.box .row.content {
		flex: 1 1 auto;
		overflow-y: scroll;
		overflow-x: hidden;
		max-height: 65vh;
		min-height: 10vh;
	}

	.box .row.footer .edit {
		width: 100%;
		flex: 1 1 auto;
		background: #fff0f044;
		border: #666;
		border-width: 1px;
		padding: 5px;
	}
	.edit textarea {
		width: 100%;
		min-height: 5em;
	}

	.estimate {
		font-size: x-small;
		font-weight: 200;
		align-content: flex-start;
		color: #554949;
		word-wrap: anywhere;
		text-align: right;
	}

	.box .row.footer {
		background: #eeeeee22;
		flex: 0 1 auto;
		display: flex;
		flex-direction: row;
		height: auto;
		resize: none;
		padding: 10px;
	}

	.deleteMe {
		position: relative;
		overflow: visible;
		height: 0px;
		left: 30px;
		top: -45px;
	}
	.deleteMe button {
		background-color: rgb(233, 138, 138); /* Green */
		padding: 5px;
		border-radius: 40%;
	}
	.send {
		align-content: center;
		padding: 5px;
	}

	.auth {
		align-content: center;
		padding: 10px;
	}
	.auth img {
		border-radius: 20%;
	}

	button {
		background-color: #a45eb6; /* Green */
		border: none;
		color: white;
		padding: 10px;
		border-radius: 20px;
		text-align: center;
		text-decoration: none;
		display: inline-block;
		font-size: 16px;
	}

	.switch {
		position: relative;
		display: inline-block;
		width: 60px;
		height: 34px;
	}

	/* Hide default HTML checkbox */
	.switch input {
		opacity: 0;
		width: 0;
		height: 0;
	}

	/* The slider */
	.slider {
		position: absolute;
		cursor: pointer;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: #ccc;
		-webkit-transition: 0.4s;
		transition: 0.4s;
	}

	.slider:before {
		position: absolute;
		content: '';
		height: 26px;
		width: 26px;
		left: 4px;
		bottom: 4px;
		background-color: white;
		-webkit-transition: 0.4s;
		transition: 0.4s;
	}

	input:checked + .slider {
		background-color: #a45eb6;
	}

	input:focus + .slider {
		box-shadow: 0 0 1px #a45eb6;
	}

	input:checked + .slider:before {
		-webkit-transform: translateX(26px);
		-ms-transform: translateX(26px);
		transform: translateX(26px);
	}

	/* Rounded sliders */
	.slider.round {
		border-radius: 34px;
	}

	.slider.round:before {
		border-radius: 50%;
	}
</style>
